import { useEffect, useState } from 'preact/hooks';
import { useI18n } from '../i18n';
import { collectFingerprint, requestMagicLink, verifyMagicLinkCode } from '../lib/api';
import { useAuth } from '../lib/auth';

export function Login() {
  const { t, locale, localePath } = useI18n();
  const { status: authStatus, user, hydrate } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<'idle' | 'sent' | 'error'>('idle');
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [codeErr, setCodeErr] = useState(false);

  // Already signed in → bounce away from the login page. Admins go to
  // /admin, everyone else to home (or back to wherever they came from).
  useEffect(() => {
    if (authStatus !== 'signed-in' || !user) return;
    const url = new URL(location.href);
    const ret = url.searchParams.get('return');
    if (ret && ret.startsWith('/') && !ret.startsWith('//')) {
      location.replace(ret);
    } else {
      // Everyone lands on the game by default — admins/partners reach their
      // areas via the header menu, so they can see and test predictions too.
      location.replace(localePath('/play'));
    }
  }, [authStatus, user]);

  // Stash the ?return target so the OAuth round-trip (which doesn't
  // preserve query) can read it back on the callback.
  useEffect(() => {
    const ret = new URL(location.href).searchParams.get('return');
    if (ret) sessionStorage.setItem('bc_return_to', ret);
  }, []);

  // Build an OAuth start URL that also propagates `?return=…` through the
  // backend's redirect cookie so the post-OAuth landing matches what the
  // user wanted in the first place. Same-origin paths only — anything else
  // is silently dropped. Returns an absolute URL so the SPA router doesn't
  // mistake the click for an in-app navigation (which would land us on
  // /it/api/auth/google, not the backend).
  function oauthHref(path: string): string {
    const ret = new URL(location.href).searchParams.get('return') || '';
    const safe = ret.startsWith('/') && !ret.startsWith('//') ? ret : '';
    const base = location.origin + path;
    return safe ? `${base}?redirect=${encodeURIComponent(location.origin + safe)}` : base;
  }

  // Force a full-page navigation. preact-iso intercepts same-origin anchor
  // clicks and reroutes them through the SPA; for /api/* we need the browser
  // to actually hit the backend.
  function openOAuth(e: Event, href: string) {
    e.preventDefault();
    location.assign(href);
  }

  async function onSubmit(e: Event) {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await requestMagicLink(email.trim(), locale, collectFingerprint());
      setStatus('sent');
    } catch {
      setStatus('error');
    } finally {
      setLoading(false);
    }
  }

  // Verify the typed 6-digit code. On success hydrate the session (sets the
  // bc_refresh cookie server-side) and bounce to wherever the user was
  // headed — the same destination the magic-link callback would land on.
  async function onVerifyCode(e: Event) {
    e.preventDefault();
    const c = code.replace(/\D/g, '');
    if (c.length !== 6 || verifying) return;
    setVerifying(true);
    setCodeErr(false);
    try {
      await verifyMagicLinkCode(email.trim(), c, collectFingerprint());
      await hydrate();
      const url = new URL(location.href);
      const ret = url.searchParams.get('return');
      if (ret && ret.startsWith('/') && !ret.startsWith('//')) {
        location.replace(ret);
      } else {
        location.replace(localePath('/play'));
      }
    } catch {
      setCodeErr(true);
    } finally {
      setVerifying(false);
    }
  }

  return (
    <div class="login-page">
      <div class="login-card">
        <h1>
          {t('login.title_a')} <em>{t('login.title_b')}</em>
        </h1>
        <p class="lede">{t('login.lede')}</p>

        <form onSubmit={onSubmit}>
          <label for="email">{t('login.email_label')}</label>
          <input
            id="email"
            type="email"
            placeholder={t('login.email_placeholder')}
            value={email}
            required
            disabled={status === 'sent'}
            onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
          />
          <button type="submit" class="primary" disabled={loading || status === 'sent'}>
            {t('login.email_cta')}
          </button>
        </form>

        {status === 'sent' && (
          <>
            <p class="login-status">{t('login.email_sent')}</p>
            <form onSubmit={onVerifyCode} class="login-code">
              <label for="code">{t('login.code_label')}</label>
              <input
                id="code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="000000"
                value={code}
                onInput={(e) =>
                  setCode((e.target as HTMLInputElement).value.replace(/\D/g, '').slice(0, 6))
                }
              />
              <button
                type="submit"
                class="primary"
                disabled={verifying || code.replace(/\D/g, '').length !== 6}
              >
                {verifying ? t('login.confirming') : t('login.code_cta')}
              </button>
              {codeErr && <p class="login-status err">{t('login.code_error')}</p>}
            </form>
          </>
        )}
        {status === 'error' && (
          <p class="login-status err">{t('login.error')}</p>
        )}

        <div class="login-divider">{t('login.or')}</div>

        <div class="login-oauth">
          <a href={oauthHref('/api/auth/google')} onClick={(e) => openOAuth(e, oauthHref('/api/auth/google'))}>
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.1 8 3l5.7-5.7C34.7 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
              <path fill="#FF3D00" d="M6.3 14.1l6.6 4.8C14.6 15.2 18.9 12 24 12c3.1 0 5.9 1.1 8 3l5.7-5.7C34.7 6.1 29.6 4 24 4 16.3 4 9.7 8.3 6.3 14.1z"/>
              <path fill="#4CAF50" d="M24 44c5.4 0 10.3-2.1 14-5.4l-6.5-5.5C29.6 34.9 26.9 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.6 39.6 16.3 44 24 44z"/>
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.5 5.5C41.1 35.6 44 30.3 44 24c0-1.3-.1-2.4-.4-3.5z"/>
            </svg>
            {t('login.google')}
          </a>
          <a href={oauthHref('/api/auth/apple')} class="apple" onClick={(e) => openOAuth(e, oauthHref('/api/auth/apple'))}>
            <svg width="14" height="16" viewBox="0 0 22 26" aria-hidden="true">
              <path fill="#fff" d="M17.6 13.6c0-3 2.5-4.5 2.6-4.6-1.4-2.1-3.6-2.4-4.4-2.4-1.9-.2-3.6 1.1-4.6 1.1-1 0-2.4-1.1-4-1.1-2 0-3.9 1.2-4.9 3C.2 12.3 1.7 18 3.7 21c1 1.5 2.2 3.2 3.7 3.1 1.5-.1 2.1-1 3.9-1 1.8 0 2.3 1 3.9 1 1.6 0 2.7-1.5 3.7-3 1.2-1.7 1.6-3.4 1.7-3.5-.1 0-3.2-1.3-3.3-5z"/>
              <path fill="#fff" d="M14.7 4.5c.8-1 1.4-2.4 1.3-3.8-1.2.1-2.7.8-3.5 1.8-.8.9-1.5 2.3-1.3 3.7 1.4.1 2.7-.7 3.5-1.7z"/>
            </svg>
            {t('login.apple')}
          </a>
        </div>

        <a class="login-back" href={localePath()}>← BeerCup</a>
      </div>
    </div>
  );
}
